import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AppLayout from './components/AppLayout';
import SiteMetadata from './components/SiteMetadata';
import useAuth from './hooks/useAuth';
import useQueue from './hooks/useQueue';
import CalendarPage from './pages/CalendarPage';
import ContextSearchPage from './pages/ContextSearchPage';
import DailyBriefingPage from './pages/DailyBriefingPage';
import FamilyPage from './pages/FamilyPage';
import HistoryPage from './pages/HistoryPage';
import ListsPage from './pages/ListsPage';
import LittleAddaPage from './pages/LittleAddaPage';
import LoginPage from './pages/LoginPage';
import NotificationsPage from './pages/NotificationsPage';
import QueuePage from './pages/QueuePage';
import QueueResultPage from './pages/QueueResultPage';
import SignupPage from './pages/SignupPage';
import TasksPage from './pages/TasksPage';
import BugReportPage from './pages/BugReportPage';
import AboutPage from './pages/AboutPage';
import FaqPage from './pages/FaqPage';
import PrivacySecurityPage from './pages/PrivacySecurityPage';
import PublicUseCasePage, { useCasePages } from './pages/PublicUseCasePage';

function RequireAuth({ children, currentUser, auth }) {
  const location = useLocation();

  if (!currentUser) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return (
    <AppLayout currentUser={currentUser} onLogout={auth.handleLogout}>
      {children}
    </AppLayout>
  );
}

function SignedInDefaultRoute({ currentUser }) {
  const location = useLocation();
  const inviteToken = new URLSearchParams(location.search).get('inviteToken');

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (inviteToken) {
    return <Navigate to={`/family?inviteToken=${encodeURIComponent(inviteToken)}`} replace />;
  }

  return <Navigate to="/history" replace />;
}

function AppShell() {
  const auth = useAuth();
  const queue = useQueue();
  const location = useLocation();
  const requestedPath =
    typeof location.state?.from === 'string' &&
    location.state.from.startsWith('/') &&
    !location.state.from.startsWith('//')
      ? location.state.from
      : '/history';

  return (
    <>
      <SiteMetadata />
      <Routes>
      <Route
        path="/login"
        element={
          auth.currentUser ? (
            <Navigate to={requestedPath} replace />
          ) : (
            <LoginPage auth={auth} />
          )
        }
      />
      <Route
        path="/signup"
        element={auth.currentUser ? <Navigate to="/history" replace /> : <SignupPage auth={auth} />}
      />
      <Route
        path="/queue"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <QueuePage auth={auth} queue={queue} />
          </RequireAuth>
        }
      />
      <Route
        path="/queue/:queueId"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <QueueResultPage auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="/bugs/:reportId"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <BugReportPage />
          </RequireAuth>
        }
      />
      <Route
        path="/notifications"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <NotificationsPage auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="/history"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <HistoryPage auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="/search"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <ContextSearchPage auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="/briefing"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <DailyBriefingPage auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="/lists"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <ListsPage auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="/lists/:scope/:listId"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <ListsPage auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="/tasks"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <TasksPage auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="/calendar"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <CalendarPage auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="/family"
        element={
          <RequireAuth currentUser={auth.currentUser} auth={auth}>
            <FamilyPage auth={auth} />
          </RequireAuth>
        }
      />
      <Route path="/" element={<LittleAddaPage currentUser={auth.currentUser} />} />
      <Route path="/school-email-assistant" element={<PublicUseCasePage content={useCasePages.schoolEmail} />} />
      <Route path="/family-calendar-app" element={<PublicUseCasePage content={useCasePages.familyCalendar} />} />
      <Route path="/shared-family-task-manager" element={<PublicUseCasePage content={useCasePages.sharedTasks} />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy-and-security" element={<PrivacySecurityPage />} />
      <Route path="/little-adda" element={<Navigate to="/" replace />} />
      <Route
        path="/app"
        element={
          <SignedInDefaultRoute currentUser={auth.currentUser} />
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default AppShell;
